import { Badge, Button, Spinner } from '@mcgames/ui';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import type { ImportInfo, ImportStatus } from '@mcgames/contracts';
import { importReasonDetail, importStatusText, importStatusTone } from '../indexer/import-text.ts';
import { CancelImportButton } from './CancelImportButton.tsx';
import { CandidatePicker } from './CandidatePicker.tsx';
import { DuplicateActions } from './DuplicateActions.tsx';
import { ErrorText } from './ErrorText.tsx';
import { ImportStepsForm } from './ImportStepsForm.tsx';
import {
  isImportShown,
  useChooseCandidate,
  useLatestImport,
  useRefreshGameImport,
  useRetryImport,
} from './import-hooks.ts';
import styles from './Games.module.css';

/** The latest indexer import of a game: its state, why it stopped, and how to continue it. */
export function ImportPanel({ gameId, gameTitle }: { gameId: string; gameTitle: string }) {
  const { t } = useTranslation('admin');
  const latest = useLatestImport(gameId);
  const info = latest.data ?? null;
  const refresh = useRefreshGameImport(gameId);
  const previous = useRef<ImportStatus | null>(null);
  const status = info?.status ?? null;

  // An import changes the game when it finishes, so the editor reloads it then.
  useEffect(() => {
    if (previous.current === 'running' && status !== null && status !== 'running') void refresh();
    previous.current = status;
  }, [status]);

  if (info === null || !isImportShown(info)) return null;

  const stopped = info.status === 'needs_review' || info.status === 'failed';
  const detail = importReasonDetail(info);
  return (
    <section className={styles['importPanel']} aria-labelledby={`import-${info.id}`}>
      <h2 id={`import-${info.id}`} className={styles['subheading']}>
        {t('games.import.title')}
      </h2>
      <div className={styles['row']}>
        <Badge tone={importStatusTone(info.status)}>
          <span role="status">{importStatusText(t, info)}</span>
        </Badge>
        {info.status === 'running' && <Spinner />}
        <span className={styles['importRelease']}>{info.releaseTitle}</span>
      </div>
      {stopped && (
        <p className={styles['hint']}>
          {t('games.import.stoppedAt', { step: t(`indexer.step.${info.step}`) })}
        </p>
      )}
      {detail !== null && <p className={styles['importDetail']}>{detail}</p>}
      {info.reason === 'already_in_catalog' && info.reasonDetail !== null && (
        <p className={styles['muted']}>
          {info.duplicateGameId !== null ? (
            <Link to={`/admin/games/${encodeURIComponent(info.duplicateGameId)}`}>
              {t('games.import.existingGame', { title: info.reasonDetail })}
            </Link>
          ) : (
            t('games.import.existingGame', { title: info.reasonDetail })
          )}
        </p>
      )}
      {info.status === 'running' && (
        <div className={styles['row']}>
          <CancelImportButton key={info.id} info={info} />
        </div>
      )}
      {stopped && (
        <StoppedActions key={info.id} gameId={gameId} gameTitle={gameTitle} info={info} />
      )}
    </section>
  );
}

function StoppedActions({
  gameId,
  gameTitle,
  info,
}: {
  gameId: string;
  gameTitle: string;
  info: ImportInfo;
}) {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const retry = useRetryImport(gameId, info.id);
  const choose = useChooseCandidate(gameId, info.id);

  if (info.reason === 'already_in_catalog') {
    // A discard may delete this game (the import's draft): the editor follows the import.
    return (
      <DuplicateActions
        info={info}
        onDone={(result) => {
          if (result.gameId !== gameId) {
            void navigate(
              result.gameId === null
                ? '/admin/games'
                : `/admin/games/${encodeURIComponent(result.gameId)}`,
            );
          }
        }}
      />
    );
  }

  return (
    <>
      <div className={styles['row']}>
        <Button variant="secondary" disabled={retry.isPending} onClick={() => retry.mutate()}>
          {t('games.import.retry')}
        </Button>
      </div>
      <ErrorText error={retry.error} />
      {info.reason === 'no_unique_match' && (
        <CandidatePicker
          candidates={info.candidates}
          initialQuery={gameTitle}
          busy={choose.isPending}
          error={choose.error}
          onPick={(choice) => choose.mutate(choice)}
        />
      )}
      {info.reason === 'archive_unknown' && info.step === 'detect' && (
        <ImportStepsForm gameId={gameId} info={info} />
      )}
    </>
  );
}
